import { Timestamp, WeekDays } from '@griply/types';
import {
  differenceInWeeks,
  endOfWeek,
  format,
  isSameWeek,
  startOfMonth,
} from 'date-fns';

import { weekdaysNumberMap } from './constants';

export type GetDateXAxisLabelOptions = {
  startDate: Timestamp;
  endDate: Timestamp;
  weekStartsOn: WeekDays;
};

export const dateTickFormatterWeekDay = (date: Timestamp) =>
  format(date, 'iiiii');

export const dateTickFormatterDay = (date: Timestamp) => format(date, 'd');

export const dateTickFormatterMonth = (
  date: Timestamp,
  weekStartsOn: WeekDays,
) => {
  const dateFnsWeekOptions = { weekStartsOn: weekdaysNumberMap[weekStartsOn] };
  const endOfWeekDate = endOfWeek(date, dateFnsWeekOptions);

  if (isSameWeek(date, startOfMonth(endOfWeekDate), dateFnsWeekOptions)) {
    return format(endOfWeekDate, 'LLLLL');
  }

  return '';
};

/**
 * Picks the right x-axis label format for a habit bar chart based on the
 * displayed timeframe:
 * - ≤ 1 week: single-letter weekday (M, T, W, ...)
 * - < 11 weeks: day number (1, 2, ..., 31)
 * - ≥ 11 weeks: single-letter month at the first week that rolls into a
 *   new month (J, F, M, ...), empty string for the other weeks
 */
export const getDateXAxisLabel = (
  date: Timestamp,
  { startDate, endDate, weekStartsOn }: GetDateXAxisLabelOptions,
) => {
  const difInWeeks = differenceInWeeks(endDate, startDate);

  return difInWeeks <= 1
    ? dateTickFormatterWeekDay(date)
    : difInWeeks < 11
      ? dateTickFormatterDay(date)
      : dateTickFormatterMonth(date, weekStartsOn);
};
